import { GetResult } from '@capacitor/preferences';

/**
 * Enum of keys used for various pieces of data that are stored.
 */
export enum StorageKeys {
    /**
     * The selected branch of the technician. Stored to determine if the branch select page needs
     * to be displayed upon logging in.
     */

    /**
     * The key for the dictionary used for translations.
     */
    DICTIONARY = 'dictionary',

    SELECTED_BRANCH = 'selectedBranch',
    /**
     * The last logged-in user ID.
     */
    USER_ID = 'userId',
    /**
     * The last logged-in username.
     */
    USERNAME = 'username',
    /**
     * The previously logged-in username.
     */
    OLD_USERNAME = 'oldusername',
    /**
     * The last logged-in user's display name.
     */
    DISPLAY_NAME = 'displayName',
    /**
     * The Tenant of the logged user.
     */
    TENANT_ID = 'tenantId',
    /**
     * The Role of the logged user.
     */
    ROLES = 'roles',

    /**
     * The effective permission keys granted to the logged user for their current branch.
     */
    PERMISSIONS = 'permissions',

    /**
     * The selected language of the logged user. On a fresh install with no prior pick, this slot is
     * populated by the background fetch in {@link ApplicationContextStore} with the backend's
     * IsDefault=true entry; once written, subsequent boots reuse it without re-fetching, so a later
     * deployment-default change does not silently flip the language on existing installs.
     */
    LANGUAGE_SELECTED_KEY = 'current_language',
    /**
     * A constant variable that represents the string 'backendType'.
     */
    /**
     * The key for the selected theme.
     */
    SELECTED_THEME = 'selectedTheme',

    /**
     * A constant variable that represents the string 'backendType'.
     */
    BACKEND_TYPE = 'backendType',

    /**
     * A constant variable that represents the string 'features'.
     */
    FEATURES = 'features',

    /**
     * The effective branding for the current tenant/branch.
     */
    BRANDING = 'branding',

    /**
     * The country code of the logged user.
     */
    COUNTRY_CODE = 'countryCode',

    /**
     * The tenant's settings for the currently-selected branch, stored as a key/value map
     * (SettingKey member NAME -> string value). Branch-scoped: derived from the selected branch's
     * own `settings` array whenever the selected branch changes. Derived flags such as the
     * inspection review gate are read from this map. The persisted value is a fast initial hint only;
     * the branch is the authority.
     */
    SETTINGS = 'settings',

    /**
     * The access code id of the last customer link opened on this device. Not a credential: the
     * code or registration is still required. Kept so a customer who lands on a report route
     * without a session is sent back to verify that link rather than to the staff sign-in.
     */
    ACCESS_CODE_ID = 'accessCodeId',
}

export enum FeatureConfigKeys {
    /**
     * A constant variable that represents the string 'dviFeatureConfig'.
     */
    DVI_FEATURE_CONFIG = 'dviFeatureConfig',
}

/**
 * Enumeration for Boolean storage keys.
 * This enum provides a set of predefined constant keys used for storing boolean values.
 */
export enum BooleanStorageKeys {
    /**
     * Represents a constant that indicates if the current user is restricted to a single branch.
     */
    IS_SINGLE_BRANCH_USER = 'isSingleBranchUser',

    /**
     * Indicates if the web content should be displayed in a fluid layout.
     */
    IS_WEB_CONTENT_FLUID = 'isWebContentFluid',

    /**
     * Indicates if the user is currently in web view mode.
     */
    IS_WEB_VIEW_MODE = 'isWebViewMode',

    /**
     * Indicates if the tenant is a standalone tenant.
     */
    IS_STANDALONE_TENANT = 'isStandaloneTenant',

    /**
     * A constant variable that represents the string if the logged user is clocked in for the workday.
     */
    IS_WORKDAY_CLOCKED_IN = 'IsWorkdayClockedIn',
}

export abstract class StorageService {
    abstract setString(key: string, value: string): Promise<void>;
    abstract getString(key: string): Promise<GetResult>;
    abstract setBoolean(key: BooleanStorageKeys, value: boolean): Promise<void>;
    abstract getBoolean(key: BooleanStorageKeys, defaultValue?: boolean): Promise<boolean>;
    abstract setObject<T>(key: string, value: T): Promise<void>;
    abstract getObject<T>(key: string): Promise<T | null>;
    abstract removeItem(key: string): Promise<void>;
    abstract clear(): Promise<void>;
    abstract setStorageType(type: StorageType): void;
}

export enum StorageType {
    LOCAL,
    SESSION,
}
